/**
 * Scan-to-scan registration: align one loaded cloud onto another.
 *
 * Written for the case X3A archives create — several stations of the same site,
 * each expressed relative to its own capture position, with no pose anywhere in
 * the container to recover the relationship from (see docs/BACKLOG.md). It is
 * not specific to X3A though; the inputs are plain world-space coordinates, so
 * any two overlapping clouds work.
 *
 * Three stages, deliberately separable because they fail differently:
 *
 * - `fitCorrespondences` — manual pairs. Always works, needs a human.
 * - `coarseAlign` — automatic, exploits that terrestrial scans are level.
 * - `icpRefine` — refinement; needs a start already close to right.
 *
 * `registerPair` runs coarse-then-ICP, which is the normal path.
 *
 * **All of this is Rust.** The solvers live in
 * `wasm/pointcloud-parser/src/registration/` and are tested there with
 * `cargo test`; this module is the marshalling layer and nothing else. In a
 * browser the work runs in `registrationWorker.ts`, so a sweep never blocks the
 * render thread; the extension host has no `Worker`, but it is already a
 * separate process from the webview, so it calls the same wasm inline.
 */
import * as THREE from 'three';
// Browser builds replace this exact request with wasmLoader.browser.ts. Keeping
// a real relative Node target is important: webpack may legally leave an
// unresolved package-import (`#...`) as a runtime missing-module stub, which
// makes the extension fail during activation rather than during registration.
import { loadRegistrationWasm } from './wasmLoader';
let worker = null;
let workerUnavailable = false;
let nextRequestId = 1;
const pending = new Map();
let workerReady = null;
let extensionPostMessage = null;
let nextExtensionRequestId = 1;
const extensionPending = new Map();
/**
 * Routes registration through the VS Code extension host.
 *
 * A webview cannot construct the same-origin worker used by the standalone
 * viewer. Running several seconds of ICP inline made the button look broken
 * because rendering and status updates froze until it returned. The extension
 * host is already a separate process, so it is the natural worker there.
 */
export function configureRegistrationExtensionHost(postMessage) {
    extensionPostMessage = postMessage;
}
/** Resolves a request previously sent by `configureRegistrationExtensionHost`. */
export function handleRegistrationExtensionResult(message) {
    if (typeof message.id !== 'string') {
        return false;
    }
    const request = extensionPending.get(message.id);
    if (!request) {
        return false;
    }
    extensionPending.delete(message.id);
    if (message.error) {
        request.reject(new Error(message.error));
    }
    else {
        const matrix = message.matrix ? Float64Array.from(message.matrix) : null;
        request.resolve(decode(matrix, message.stats ?? null));
    }
    return true;
}
/**
 * Resolves to a worker that has already loaded its wasm, or null when this
 * environment cannot run one.
 *
 * The readiness handshake is what makes the *first* attempt work everywhere. A
 * failing worker only reports through `onerror`, asynchronously, so without
 * waiting for it we would transfer the point buffers into a worker that never
 * answers — and the buffers, being transferred, are gone by the time we learn
 * that, leaving nothing to retry with in-page.
 */
async function ensureWorker() {
    // `document` rather than `Worker` alone: this module is also loaded by the
    // extension host and by Node, and the dynamic import below must not be
    // evaluated there (see workerHost.ts).
    if (workerUnavailable || typeof Worker === 'undefined' || typeof document === 'undefined') {
        return null;
    }
    if (workerReady) {
        return workerReady;
    }
    workerReady = startWorker();
    return workerReady;
}
async function startWorker() {
    try {
        const { createRegistrationWorker } = await import('./workerHost');
        worker = createRegistrationWorker();
    }
    catch (error) {
        console.warn('[registration] worker unavailable, running in page:', error);
        workerUnavailable = true;
        return null;
    }
    const handshake = await new Promise(resolve => {
        const settle = (value) => {
            clearTimeout(timer);
            resolve(value);
        };
        const timer = setTimeout(() => settle(false), 10000);
        worker.addEventListener('message', function onReady(event) {
            if (typeof event.data?.ready !== 'boolean') {
                return;
            }
            worker.removeEventListener('message', onReady);
            settle(event.data.ready === true);
        });
        worker.addEventListener('error', () => settle(false), { once: true });
    });
    if (!handshake) {
        console.warn('[registration] worker did not start, running in page instead');
        worker?.terminate();
        worker = null;
        workerUnavailable = true;
        return null;
    }
    worker.onmessage = event => {
        const request = pending.get(event.data.id);
        if (!request) {
            return;
        }
        if (typeof event.data?.ready === 'boolean') {
            return;
        }
        pending.delete(event.data.id);
        if (event.data.error) {
            request.reject(new Error(event.data.error));
            return;
        }
        request.resolve(decode(event.data.matrix, event.data.stats));
    };
    worker.onerror = event => {
        const error = new Error(event.message || 'Registration worker failed');
        for (const request of pending.values()) {
            request.reject(error);
        }
        pending.clear();
        worker?.terminate();
        worker = null;
        // Fall back to running inline rather than leaving the feature broken.
        workerUnavailable = true;
    };
    return worker;
}
function decode(matrix, stats) {
    if (!matrix || matrix.length !== 16) {
        return null;
    }
    const parsed = stats ? JSON.parse(stats) : {};
    return {
        // Rust emits column-major, matching THREE.Matrix4.elements, so this is a
        // copy and not a transpose.
        matrix: new THREE.Matrix4().fromArray(Array.from(matrix)),
        coarse: parsed.coarse
            ? {
                yawDegrees: parsed.coarse.yawDegrees,
                score: parsed.coarse.score,
                runnerUpScore: parsed.coarse.runnerUpScore,
                candidates: (parsed.coarse.candidates ?? []).map((candidate) => ({
                    yawDegrees: candidate.yawDegrees,
                    score: candidate.score,
                    matrix: new THREE.Matrix4().fromArray(candidate.matrix),
                })),
            }
            : null,
        icp: parsed.icp ?? null,
        candidateIndex: parsed.candidateIndex ?? -1,
        candidatesTried: parsed.candidatesTried ?? 0,
        rmse: parsed.rmse ?? null,
        maxError: parsed.maxError ?? null,
        startedFrom: parsed.startedFrom ?? 'none',
        sourceConditioning: parsed.sourceConditioning ?? 1,
        voxelCell: parsed.voxelCell ?? 0,
    };
}
async function dispatch(kind, source, target, settings) {
    const settingsJson = JSON.stringify(settings ?? {});
    if (extensionPostMessage) {
        const id = `registration-${nextExtensionRequestId++}`;
        return new Promise((resolve, reject) => {
            extensionPending.set(id, { resolve, reject });
            extensionPostMessage({
                type: 'registrationRequest',
                id,
                kind,
                source,
                target,
                settingsJson,
            });
        });
    }
    const host = await ensureWorker();
    if (!host) {
        const wasm = await loadRegistrationWasm();
        if (!wasm) {
            throw new Error('Registration is unavailable: the WebAssembly module failed to load.');
        }
        const result = kind === 'register'
            ? wasm.register_pair(source, target, settingsJson)
            : kind === 'coarse'
                ? wasm.coarse_align(source, target, settingsJson)
                : kind === 'icp'
                    ? wasm.icp_refine(source, target, settingsJson)
                    : wasm.fit_correspondences(source, target);
        if (!result) {
            return null;
        }
        try {
            return decode(result.matrix, result.stats);
        }
        finally {
            result.free?.();
        }
    }
    const id = nextRequestId++;
    const request = { id, kind, source, target, settingsJson };
    return new Promise((resolve, reject) => {
        pending.set(id, { resolve, reject });
        // The point buffers are copies made for this call, so transferring them
        // costs nothing and saves duplicating tens of megabytes per request.
        host.postMessage(request, [source.buffer, target.buffer]);
    });
}
function icpSettings(options) {
    const { initial, ...rest } = options;
    return initial ? { ...rest, initial: Array.from(initial.elements) } : rest;
}
/** Coarse sweep then ICP refinement — the normal automatic path. */
/**
 * Which execution path the last call used, for the panel to report.
 *
 * Worth surfacing: "worker" and "in page" behave differently — the second
 * blocks the viewer while it runs — and when registration breaks, knowing which
 * one was in play is the difference between a diagnosis and a guess.
 */
export function registrationBackend() {
    if (extensionPostMessage) {
        return 'extension host';
    }
    if (worker) {
        return 'worker';
    }
    return workerUnavailable ? 'in page' : 'not started';
}
export function registerPair(source, target, options = {}) {
    return dispatch('register', source, target, {
        coarse: options.coarse === false ? undefined : (options.coarse ?? {}),
        icp: options.icp === false ? undefined : icpSettings(options.icp ?? {}),
        maxCandidates: options.maxCandidates,
        extraStarts: options.extraStarts?.map(matrix => Array.from(matrix.elements)),
    });
}
/** The yaw sweep alone, for callers that want the shortlist without refining. */
export function coarseAlign(source, target, options = {}) {
    return dispatch('coarse', source, target, options);
}
/** ICP refinement alone, from `options.initial` (identity when absent). */
export function icpRefine(source, target, options = {}) {
    return dispatch('icp', source, target, icpSettings(options));
}
/**
 * Closed-form fit from matched correspondences. `source` and `target` are flat
 * xyz triples of equal length in matching order; at least three pairs.
 */
export function fitCorrespondences(source, target) {
    return dispatch('fit', source, target, {});
}
